import { useState, useCallback, useEffect } from 'react';
import { mfaService } from '@/features/auth/services/mfaService';
import type { MfaStep, UseMfaProps } from '@/features/auth/types/mfa.types';
import { useSpinner } from '@chikara/ui';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { useNavigate } from 'react-router-dom';
import * as ROUTES from '@/shared/constants/routes';
import { t } from '@/i18n';

/** Slug del comando `mfa-setup` (path `/commands/mfa-setup`) */
const MFA_SETUP_COMMAND = 'mfa-setup';
/** Tipo de challenge; si el BFF exige otro valor, cambiar solo aquí */
const MFA_SETUP_TYPE = 'mfa-setup';

export function useMfa(onMfaSuccess: () => void): UseMfaProps {
  const navigate = useNavigate();
  const [step, setStep] = useState<MfaStep>(t('mfaStep.intro'));
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState<string>('');
  const [isExpired, setIsExpired] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const { show, hide, isSpinnerLoading: isLoading } = useSpinner();

  const loadQr = useCallback(
    async () => {
      setError(null);
      // Onboarding por QR: no mezclar con un challenge previo de `mfa-send` (login)
      tokenStorage.clearMfaChallengeContext();
      show();

      try {
        const result = await mfaService.mfaSetup();
        tokenStorage.setSetupToken(result.data.mfa_token);

        setQrCode(result.data.qr);
        setManualCode(result.data.key);
        setStep(t('mfaStep.qr'));
      } catch {
        setError(t('mfaError.error'));
      } finally {
        hide();
      }
    }, []
  );

  const startVerification = () => {
    setStep(t('mfaStep.verify'));
  };

  const verifyOtp = async (otp: string) => {
    show();
    setError(null);

    try {
      const setupToken = tokenStorage.getSetupTokenOptional();
      const challenge = tokenStorage.getMfaChallengeContextOptional();

      let data: { mfa_token: string; expires_at?: string, status?: string };

      // Challenge de `mfa-send` (login, etc.): mismo command/type que el send; prioridad sobre setup_token residual
      if (challenge) {
        ({ data } = await mfaService.mfaVerify(otp, challenge));
        tokenStorage.clearMfaChallengeContext();
      } else if (setupToken) {
        ({ data } = await mfaService.mfaValidate({
          command: MFA_SETUP_COMMAND,
          type: MFA_SETUP_TYPE,
          otp,
          mfa_token: setupToken,
        }));
      } else {
        setError(t('mfaError.error'));
        return;
      }
      // El proxy puede no devolver `mfa_token` en el response; el siguiente comando espera el mismo token usado en el verify.
      const nextMfaToken = data?.mfa_token ?? challenge?.mfa_token ?? setupToken ?? null;
      if (!nextMfaToken) {
        setError(t('mfaError.error'));
        return;
      }
      tokenStorage.setMfaToken(nextMfaToken);
      if (data.expires_at) {
        tokenStorage.setMfaExpiresAt(data.expires_at);
      }

      onMfaSuccess();
    } catch (err: any) {
      if (err?.code === '007.011.422' || err?.code === '007.005.422') {
        return navigate(ROUTES.LOGIN);
      }
      setError(t('mfaError.code'));
    } finally {
      hide();
    }
  };

  const returnIntro = () => {
    setQrCode('');
    setManualCode('');
    setStep(t('mfaStep.intro'));
  };

  useEffect(() => {
    const expiresAt = tokenStorage.getExpiresAt();

    if (!expiresAt) return;

    const isExpired = Date.now() > Number(expiresAt);

    if (isExpired) {
      setIsExpired(true);
      setError(t('mfaError.expiredCode'));
      setStep(t('mfaStep.intro'));
    }
  }, []);

  return {
    step,
    qrCode,
    manualCode,
    isExpired,
    error,
    isLoading,
    setStep,
    loadQr,
    verifyOtp,
    startVerification,
    returnIntro
  };
}
